<script setup lang="ts">
import { computed } from "vue";
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";

// Import shared components
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Image from "@/views/blocks/shared/section/image/Image.vue";

import type { WithTieredImagesProps } from "./WithTieredImagesProps";

const props = defineProps<{
    block: PageBlock<WithTieredImagesProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :background-color="styleValues.background">
        <FullSpacing class="max-w-7xl mx-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
                <!-- Left Column - Content -->
                <div class="flex flex-col justify-center">
                    <!-- Section Header -->
                    <EditableElement
                        v-if="!dataValues.tag.isHidden"
                        :blockId="block.id"
                        field-name="tag"
                    >
                        <p :style="{ color: styleValues.brand }" class="font-semibold mb-4">
                            {{ dataValues.tag.data.text }}
                        </p>
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.title.isHidden"
                        :blockId="block.id"
                        field-name="title"
                    >
                        <Heading
                            :tag="dataValues.title.data.tag"
                            :text="dataValues.title.data.text"
                            :text-color="styleValues.text"
                            class="mb-4 text-3xl sm:text-4xl"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.description.isHidden"
                        :blockId="block.id"
                        field-name="description"
                    >
                        <Paragraph
                            :text="dataValues.description.data.text"
                            :text-color="styleValues.text"
                            class="opacity-70"
                        />
                    </EditableElement>

                    <!-- Border -->
                    <div class="my-10 border-t" :style="{ borderColor: styleValues.border }" />

                    <!-- Features List -->
                    <div class="flex flex-col gap-10">
                        <EditableElement
                            v-for="feature in dataValues.features.data.items"
                            :key="feature.id"
                            :blockId="block.id"
                            :field-name="`features_${feature.id}`"
                            :hideable="false"
                        >
                            <div class="flex flex-col gap-3">
                                <Paragraph
                                    :text="feature.title"
                                    :text-color="styleValues.text"
                                    class="text-sm font-semibold"
                                />
                                <Paragraph
                                    :text="feature.description"
                                    :text-color="styleValues.text"
                                    class="text-sm opacity-70"
                                />
                            </div>
                        </EditableElement>
                    </div>
                </div>

                <!-- Right Column - Images -->
                <div class="flex flex-col justify-center gap-6 md:gap-8">
                    <!-- Main Image -->
                    <div class="w-full">
                        <EditableElement
                            v-if="!dataValues.mainImage.isHidden"
                            :blockId="block.id"
                            field-name="mainImage"
                        >
                            <Image
                                :src="dataValues.mainImage.data.src"
                                :alt="dataValues.mainImage.data.alt"
                                class="w-full object-cover rounded-lg"
                                width="700"
                                height="700"
                            />
                        </EditableElement>
                    </div>
                    <!-- Secondary Images -->
                    <div class="grid grid-cols-2 gap-6 md:gap-8">
                        <EditableElement
                            v-for="image in dataValues.secondaryImages.data.items"
                            :key="image.id"
                            :blockId="block.id"
                            :field-name="`secondaryImages_${image.id}`"
                            :hideable="false"
                        >
                            <Image
                                :src="image.imageSrc"
                                :alt="image.imageAlt"
                                class="w-full object-cover rounded-lg"
                                width="350"
                                height="350"
                            />
                        </EditableElement>
                    </div>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
